/**
 * Security & Compliance Console — admin-only tab inside /admin.
 *
 * Three panels:
 *   1. Last 100 security_events  (kind / surface / user_id / sample)
 *   2. Rate-limit rejections in last 24h  (count by endpoint)
 *   3. Users approaching data deletion  (retention_at within 30 days)
 *
 * Top-right: "DPDP Act 2023 Compliant" badge with last audit date +
 * "Acknowledge fresh audit" action (admin click stamps a new last_audit_at).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Shield, ShieldCheck, AlertTriangle, Trash2, Activity, Ban, Clock, RefreshCw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_STYLES = {
  prompt_injection_match: { bg: "#FFEBEE", fg: "#C62828", icon: AlertTriangle, label: "Prompt Injection" },
  rate_limit_exceeded: { bg: "#FFF3E0", fg: "#E65100", icon: Ban, label: "Rate Limit" },
  user_data_deletion: { bg: "#E8F5E9", fg: "#2E7D32", icon: Trash2, label: "Data Deletion" },
};

function relativeTime(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

function ComplianceBadge({ compliance, onAcknowledge, busy }) {
  if (!compliance) return null;
  const ok = compliance.status === "compliant";
  return (
    <div className="bg-white border border-navy/10 rounded-xl px-5 py-3 flex items-center gap-4 shadow-sm" data-testid="compliance-badge">
      <div className={`p-2 rounded-lg ${ok ? "bg-green-50" : "bg-amber-50"}`}>
        <ShieldCheck className={ok ? "w-5 h-5 text-green-700" : "w-5 h-5 text-amber-600"} />
      </div>
      <div className="min-w-0">
        <p className="font-playfair text-navy text-sm font-bold leading-tight">
          {compliance.framework} <span className="text-green-700">Compliant</span>
        </p>
        <p className="font-dm text-navy/50 text-[11px]">
          Last audit {compliance.last_audit_at ? relativeTime(compliance.last_audit_at) : "never"}
          {compliance.audited_by && compliance.audited_by !== "—" ? ` · ${compliance.audited_by}` : ""}
        </p>
      </div>
      <a href="/trust" target="_blank" rel="noopener noreferrer" data-testid="trust-page-link"
        className="ml-2 inline-flex items-center gap-1 font-dm text-navy/60 text-xs hover:text-gold">
        Public trust page <ExternalLink className="w-3 h-3" />
      </a>
      <button onClick={onAcknowledge} disabled={busy} data-testid="acknowledge-audit-btn"
        className="ml-2 inline-flex items-center gap-1.5 bg-navy text-cream px-3 py-1.5 rounded-lg font-dm text-xs font-bold hover:bg-navy/90 disabled:opacity-50">
        <RefreshCw className="w-3.5 h-3.5" /> {busy ? "Stamping…" : "Acknowledge audit"}
      </button>
    </div>
  );
}

function StatTile({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-white border border-navy/10 rounded-xl p-4 flex items-center gap-3">
      <div className={`p-2 rounded-lg`} style={{ background: `${color}20` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <div>
        <p className="font-playfair text-navy text-2xl font-bold leading-none">{value}</p>
        <p className="font-dm text-navy/50 text-xs uppercase tracking-wide mt-1">{label}</p>
      </div>
    </div>
  );
}

function EventsPanel({ events, onExport }) {
  return (
    <div className="bg-white rounded-2xl border border-navy/10 shadow-sm" data-testid="security-events-panel">
      <div className="px-6 py-4 border-b border-navy/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-gold" />
          <h3 className="font-playfair text-navy text-lg font-bold">Last 100 Security Events</h3>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-dm text-navy/50 text-xs">{events.length} rows</span>
          <button onClick={onExport} data-testid="export-csv-btn"
            className="inline-flex items-center gap-1.5 bg-navy text-cream px-3 py-1.5 rounded-lg font-dm text-xs font-bold hover:bg-navy/90">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-cream/60">
            <tr className="text-navy/60 font-dm text-[11px] uppercase tracking-wide">
              <th className="text-left px-4 py-2.5">When</th>
              <th className="text-left px-4 py-2.5">Kind</th>
              <th className="text-left px-4 py-2.5">Surface / Endpoint</th>
              <th className="text-left px-4 py-2.5">User</th>
              <th className="text-left px-4 py-2.5">Sample</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && (
              <tr><td colSpan={5} className="font-dm text-navy/40 text-center py-8">No events yet — the system is quiet.</td></tr>
            )}
            {events.map((e) => {
              const style = EVENT_STYLES[e.kind] || { bg: "#F0F0F0", fg: "#0F1E35", icon: Activity, label: e.kind };
              const Icon = style.icon;
              const sample = e.matched_phrase || e.sample || e.limit_detail || e.email_hash || "—";
              return (
                <tr key={e._id} className="border-t border-navy/5 hover:bg-cream/30" data-testid={`security-event-${e.kind}`}>
                  <td className="px-4 py-2.5 font-dm text-navy/70 text-xs whitespace-nowrap">{relativeTime(e.created_at)}</td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-dm text-[11px] font-bold"
                      style={{ background: style.bg, color: style.fg }}>
                      <Icon className="w-3 h-3" /> {style.label}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 font-dm text-navy text-xs">{e.surface || "—"}</td>
                  <td className="px-4 py-2.5 font-mono text-navy/50 text-[11px]">{e.user_id ? e.user_id.toString().slice(-8) : (e.ip || "—")}</td>
                  <td className="px-4 py-2.5 font-dm text-navy/70 text-xs max-w-xl truncate" title={sample}>{sample}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RateLimitPanel({ data }) {
  if (!data) return null;
  return (
    <div className="bg-white rounded-2xl border border-navy/10 shadow-sm p-6" data-testid="rate-limit-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Ban className="w-5 h-5 text-gold" />
          <h3 className="font-playfair text-navy text-lg font-bold">Rate-Limit Rejections · 24h</h3>
        </div>
        <span className="font-playfair text-navy text-2xl font-bold" data-testid="rl-total">{data.total_rejections}</span>
      </div>
      {data.by_endpoint.length === 0 ? (
        <p className="font-dm text-navy/40 text-sm">No rejections in the last 24 hours — limits are not being tripped.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-navy/50 font-dm text-[11px] uppercase tracking-wide border-b border-navy/10">
              <th className="text-left py-2">Endpoint</th>
              <th className="text-right py-2">Rejections</th>
            </tr>
          </thead>
          <tbody>
            {data.by_endpoint.map((row) => (
              <tr key={row.endpoint} className="border-b border-navy/5">
                <td className="py-2.5 font-mono text-navy text-xs">{row.endpoint}</td>
                <td className="py-2.5 font-dm text-navy font-bold text-right">{row.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DeletionsPanel({ data }) {
  if (!data) return null;
  return (
    <div className="bg-white rounded-2xl border border-navy/10 shadow-sm" data-testid="deletions-panel">
      <div className="px-6 py-4 border-b border-navy/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-gold" />
          <h3 className="font-playfair text-navy text-lg font-bold">Approaching Data Deletion · 30 days</h3>
        </div>
        <span className="font-dm text-navy/50 text-xs">{data.count} rows queued</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-cream/60">
            <tr className="text-navy/60 font-dm text-[11px] uppercase tracking-wide">
              <th className="text-left px-4 py-2.5">User</th>
              <th className="text-left px-4 py-2.5">Collection</th>
              <th className="text-left px-4 py-2.5">Deletes at</th>
              <th className="text-right px-4 py-2.5">Days left</th>
            </tr>
          </thead>
          <tbody>
            {data.items.length === 0 && (
              <tr><td colSpan={4} className="font-dm text-navy/40 text-center py-8">Nothing queued for deletion in the next 30 days.</td></tr>
            )}
            {data.items.map((row) => {
              const days = row.days_remaining ?? 0;
              const warn = days <= 7;
              return (
                <tr key={`${row.collection}-${row.doc_id}`} className="border-t border-navy/5">
                  <td className="px-4 py-2.5 font-dm text-navy text-xs">{row.user_email || (row.user_id ? row.user_id.slice(-8) : "—")}</td>
                  <td className="px-4 py-2.5 font-mono text-navy/70 text-[11px]">{row.collection}</td>
                  <td className="px-4 py-2.5 font-dm text-navy/70 text-xs whitespace-nowrap">{row.deletes_at ? new Date(row.deletes_at).toLocaleDateString() : "—"}</td>
                  <td className={`px-4 py-2.5 font-dm font-bold text-right ${warn ? "text-red-700" : "text-navy"}`}>{days}d</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function SecurityConsole() {
  const [events, setEvents] = useState([]);
  const [rateLimits, setRateLimits] = useState(null);
  const [deletions, setDeletions] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ackBusy, setAckBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      axios.get(`${API}/api/admin/security/events`, { withCredentials: true }).then((r) => r.data),
      axios.get(`${API}/api/admin/security/rate-limits-24h`, { withCredentials: true }).then((r) => r.data),
      axios.get(`${API}/api/admin/security/upcoming-deletions`, { withCredentials: true }).then((r) => r.data),
      axios.get(`${API}/api/admin/security/compliance`, { withCredentials: true }).then((r) => r.data),
    ]).then(([ev, rl, del, comp]) => {
      if (cancelled) return;
      setEvents(ev.items || []);
      setRateLimits(rl);
      setDeletions(del);
      setCompliance(comp);
    }).catch(() => { /* admin gate already enforced */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const handleAck = async () => {
    setAckBusy(true);
    try {
      await axios.post(`${API}/api/admin/security/compliance/audit`, { notes: "Console acknowledgement" }, { withCredentials: true });
      setRefreshKey((k) => k + 1);
    } catch { /* surface via no-op */ }
    finally { setAckBusy(false); }
  };

  const handleExport = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/security/events.csv?limit=100`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `roleiq-security-events-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { /* admin gate enforced */ }
  };

  const injectionsCount = events.filter((e) => e.kind === "prompt_injection_match").length;
  const deletionsCount = events.filter((e) => e.kind === "user_data_deletion").length;
  const rlCount = rateLimits?.total_rejections || 0;

  return (
    <div className="space-y-6" data-testid="security-console">
      {/* Header — title + DPDP badge */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Shield className="w-6 h-6 text-gold" />
            <h2 className="font-playfair text-navy text-2xl font-bold">Security & Compliance Console</h2>
          </div>
          <p className="font-dm text-navy/60 text-sm max-w-2xl">
            Live audit feed of every prompt-injection attempt, rate-limit rejection, and TTL-driven deletion. Use this in HR procurement conversations as your DPDP Act 2023 evidence.
          </p>
        </div>
        <ComplianceBadge compliance={compliance} onAcknowledge={handleAck} busy={ackBusy} />
      </div>

      {/* Top stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Events shown" value={events.length} color="#0F1E35" icon={Activity} />
        <StatTile label="Injection attempts" value={injectionsCount} color="#C62828" icon={AlertTriangle} />
        <StatTile label="Rate-limit 429s · 24h" value={rlCount} color="#E65100" icon={Ban} />
        <StatTile label="Data deletions" value={deletionsCount} color="#2E7D32" icon={Trash2} />
      </div>

      {loading ? (
        <p className="font-dm text-navy/60 text-center py-12">Loading security signals…</p>
      ) : (
        <>
          <EventsPanel events={events} onExport={handleExport} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RateLimitPanel data={rateLimits} />
            <DeletionsPanel data={deletions} />
          </div>
        </>
      )}
    </div>
  );
}
